import type { MaybeArray } from "models-webgl2";
import { imageSourceToCanvas } from "utils/canvas/image-source-to-canvas";
import { toArrayOptions } from "utils/helpers/to-array-options";
import { Vec2 } from "utils/math/vec2";
import { calcCropping } from "utils/math/calc-cropping";
import { Webgl2Component } from "../../webgl2-component";
import { Webgl2 } from "../../webgl2";
import { Webgl2dRenderer } from "../webgl2d-renderer";

export class RendererExport {
  constructor(private readonly _renderer: Webgl2dRenderer) {}

  public getCanvasCast() {
    return this._checkForExport(() => {
      this._renderer.renderComponents();
      return imageSourceToCanvas(Webgl2.canvas);
    });
  }

  public getComponentsCast(components: MaybeArray<Webgl2Component<any, any>>) {
    const componentsArr = toArrayOptions(components);
    const filteredArr = componentsArr.filter((component) => {
      return this._renderer.components.find((rendererRect) => rendererRect === component);
    });

    if (!filteredArr.length) return document.createElement("canvas");

    let sX: null | number = null;
    let sY: null | number = null;
    let sW: null | number = null;
    let sH: null | number = null;

    for (const component of filteredArr) {
      const tx = Math.round(component.transform.translation.x);
      const ty = Math.round(component.transform.translation.y);
      const dW = tx + Math.ceil(component.transform.size.width);
      const dH = ty + Math.ceil(component.transform.size.height);

      sX = sX === null ? tx : Math.min(tx, sX);
      sW = sW === null ? dW : Math.max(dW, sW);
      sY = sY === null ? ty : Math.min(ty, sY);
      sH = sH === null ? dH : Math.max(dH, sH);
    }

    if (sX === null || sW === null || sY === null || sH === null) {
      return document.createElement("canvas");
    }

    sW = sW - sX;
    sH = sH - sY;
    sX -= this._renderer.canvasTranslation.x;
    sY -= this._renderer.canvasTranslation.y;

    const canvasSize = this._renderer.canvasSize;

    const cropping = calcCropping(canvasSize.width, canvasSize.height, sX, sY, sW, sH);
    const croppingViewport = new Vec2(cropping.sw, cropping.sh);
    if (!cropping.sw || !cropping.sh) return document.createElement("canvas");

    return this._checkForExport(() => {
      Webgl2.camera.setTranslation(
        cropping.sx + this._renderer.canvasTranslation.x,
        cropping.sy + this._renderer.canvasTranslation.y,
      );

      for (const component of filteredArr) {
        Webgl2.renderComponent(component);
      }

      return imageSourceToCanvas(Webgl2.canvas);
    }, croppingViewport);
  }

  public getComponentsCanvasCast(components: MaybeArray<Webgl2Component<any, any>>) {
    const componentsArr = toArrayOptions(components);
    const filteredArr = componentsArr.filter((component) => {
      return this._renderer.components.find((rendererRect) => rendererRect === component);
    });

    if (!filteredArr.length) return document.createElement("canvas");

    return this._checkForExport(() => {
      for (const component of filteredArr) {
        Webgl2.renderComponent(component);
      }
      return imageSourceToCanvas(Webgl2.canvas);
    });
  }

  private _checkForExport<R>(callback: () => R, viewportSize?: Vec2): R {
    const prevViewport = Webgl2.camera.projection;
    Webgl2.camera.save();

    const gl = Webgl2.gl;
    const canvasSize = viewportSize ?? this._renderer.canvasSize;

    // Setup block
    Webgl2.setViewport(canvasSize);
    Webgl2.camera.reset(canvasSize);
    Webgl2.camera.setTranslation(this._renderer.canvasTranslation);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    const res = callback();

    gl.disable(gl.BLEND);

    Webgl2.setViewport(prevViewport);
    Webgl2.camera.restore();

    return res;
  }
}
